import React, { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Sparkles, Terminal } from "lucide-react";
import { PROFILE } from "../../data/mock";

export default function HeroPortraitFrame() {
  const shouldReduceMotion = useReducedMotion();
  const [rotate, setRotate] = useState({ x: 0, y: 0 });

  const handleMouseMove = (e) => {
    if (shouldReduceMotion) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    // Restrained subtle tilt (max ±3 degrees)
    const rotateX = ((y - centerY) / centerY) * -3;
    const rotateY = ((x - centerX) / centerX) * 3;
    setRotate({ x: rotateX, y: rotateY });
  };

  const handleMouseLeave = () => {
    setRotate({ x: 0, y: 0 });
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.96, y: 12 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ duration: 0.8, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      style={{
        transform: `perspective(1000px) rotateX(${rotate.x}deg) rotateY(${rotate.y}deg)`,
        transition: "transform 0.25s ease-out",
      }}
      className="relative w-full max-w-[340px] sm:max-w-[380px] lg:max-w-[420px] aspect-[4/5] mx-auto lg:ml-auto select-none group"
    >
      {/* 1. Restrained Ambient Rim Light Halo */}
      <div
        className="absolute -inset-1 rounded-2xl opacity-40 blur-2xl pointer-events-none transition-opacity duration-700 group-hover:opacity-65"
        style={{
          background:
            "radial-gradient(circle at 50% 28%, rgba(139,92,246,0.38) 0%, rgba(91,33,182,0.15) 55%, transparent 75%)",
        }}
      />

      {/* 2. Architectural Frame Container */}
      <div className="relative h-full w-full rounded-2xl border border-border/80 bg-gradient-to-b from-[#13111c] via-[#0b0a12] to-[#050508] overflow-hidden shadow-2xl flex flex-col justify-between">
        
        {/* Subtle Technical Corner Brackets */}
        <span className="absolute top-3 left-3 font-mono text-[11px] text-primary/70 pointer-events-none z-30" aria-hidden="true">┌</span>
        <span className="absolute top-3 right-3 font-mono text-[11px] text-primary/70 pointer-events-none z-30" aria-hidden="true">┐</span>
        <span className="absolute bottom-3 left-3 font-mono text-[11px] text-primary/70 pointer-events-none z-30" aria-hidden="true">└</span>
        <span className="absolute bottom-3 right-3 font-mono text-[11px] text-primary/70 pointer-events-none z-30" aria-hidden="true">┘</span>

        {/* Top Technical Metadata Header */}
        <div className="relative z-30 px-6 pt-3.5 flex items-center justify-between pointer-events-none">
          <div className="flex items-center gap-1.5 font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground/80">
            <Terminal className="h-3 w-3 text-primary/70" />
            <span>FMI // ARCH-ID</span>
          </div>
          <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 font-mono text-[9.5px] tracking-wider uppercase text-emerald-400">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
            <span>AVAILABLE</span>
          </div>
        </div>

        {/* 3. Internal Rim Light Directly Behind Head and Shoulders */}
        <div
          className="absolute inset-0 pointer-events-none z-10"
          style={{
            background:
              "radial-gradient(circle at 50% 28%, rgba(139,92,246,0.30) 0%, rgba(76,29,149,0.10) 48%, transparent 72%)",
          }}
        />

        {/* 4. Authentic Mid-Torso / Waist-Up Portrait Asset */}
        <div className="relative z-10 w-full h-full pt-4 flex items-start justify-center overflow-hidden">
          <img
            src={PROFILE.photo}
            alt={`Portrait of ${PROFILE.fullName}`}
            className="w-full h-full object-cover object-top filter brightness-[0.98] contrast-[1.03] transition-transform duration-700 ease-out group-hover:scale-[1.02]"
            style={{
              objectPosition: "top center",
            }}
            loading="eager"
            decoding="async"
          />
        </div>

        {/* Subtle Bottom Vignette Gradient to anchor the torso smoothly */}
        <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-[#050508] via-[#050508]/85 to-transparent z-20 pointer-events-none" />

        {/* 5. Bottom Operator Overlay Card */}
        <div className="relative z-30 p-4 pt-0">
          <div className="p-3 sm:p-3.5 rounded-xl border border-border/80 bg-background/80 backdrop-blur-md flex items-center justify-between shadow-lg">
            <div>
              <p className="font-mono text-[11px] tracking-[0.16em] uppercase text-foreground font-medium">
                {PROFILE.fullName}
              </p>
              <p className="font-mono text-[9.5px] tracking-[0.14em] uppercase text-muted-foreground mt-0.5">
                Informatics &middot; Full-Stack &middot; GMT+7
              </p>
            </div>
            <div className="h-2 w-2 rounded-full bg-primary/90 shadow-[0_0_8px_rgba(139,92,246,0.8)]" />
          </div>
        </div>

      </div>
    </motion.div>
  );
}
